import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const AppContext = createContext(null);

const PLANS_API = `${process.env.REACT_APP_BACKEND_URL}/api/plans`;

/* ── DaaS plan fallback (backend GET /api/plans is authoritative; this only
      prevents an empty UI if the fetch fails). Full feature lists come from the API. ── */
export const DAAS_PLANS_FALLBACK = {
  version: '1.0.0',
  currency: 'USD',
  commercial_rules: { standard_commitment_months: 12, payment_options: ['monthly', 'annual'], annual_discount: false },
  plans: [
    { id: 'basic', name: 'Basic', tagline: 'Your essential web app, fully managed.', monthly_usd: 290, annual_usd: 3480, quote_only: false, highlight: false },
    { id: 'premium', name: 'Premium', tagline: 'Advanced workflows, dashboards and a mobile companion.', monthly_usd: 990, annual_usd: 11880, quote_only: false, highlight: true },
    { id: 'elite', name: 'Elite', tagline: 'Custom business software with integrations and priority hours.', monthly_usd: 1490, annual_usd: 17880, quote_only: false, highlight: false },
    { id: 'custom', name: 'Custom', tagline: 'Enterprise software, large SaaS and specialized builds.', monthly_usd: null, annual_usd: null, quote_only: true, highlight: false },
  ],
};

/* ── Currency Config ── */
const PRICING = {
  PHP: { symbol: '₱', code: 'PHP' },
  USD: { symbol: '$', code: 'USD' },
};

/* ── Gig Data with dual-currency static pricing ── */
export const GIGS = [
  {
    id: 'native-app-mvp',
    title: 'Version 1 Native App MVP',
    shortDesc: 'Launch your mobile-first product in 15 days with AI-accelerated development.',
    deliveryDays: 15,
    category: 'Mobile App',
    tiers: [
      {
        name: 'Basic MVP',
        pricePHP: 124975, originalPricePHP: 249950,
        priceUSD: 2499,   originalPriceUSD: 4999,
        delivery: '15 days',
        features: [
          'Core feature set (up to 8 screens)',
          'iOS OR Android (React Native)',
          'REST API backend',
          'Basic auth & onboarding',
          '1 round of revisions',
          'Source code handoff',
        ],
      },
      {
        name: 'Pro Scaling',
        pricePHP: 249975, originalPricePHP: 499950,
        priceUSD: 4999,   originalPriceUSD: 9999,
        delivery: '21 days',
        features: [
          'Extended feature set (up to 20 screens)',
          'iOS + Android (React Native)',
          'GraphQL + REST API',
          'OAuth, push notifications, analytics',
          '3 rounds of revisions',
          'CI/CD pipeline setup',
          '30-day post-launch support',
        ],
      },
      {
        name: 'Enterprise',
        pricePHP: 499975, originalPricePHP: 999950,
        priceUSD: 9999,   originalPriceUSD: 19999,
        delivery: '30 days',
        features: [
          'Unlimited screens & features',
          'iOS + Android + Web PWA',
          'Microservices architecture',
          'SSO, RBAC, audit logging',
          'Unlimited revisions',
          'Full DevOps & monitoring',
          '90-day post-launch support',
          'Dedicated project manager',
        ],
      },
    ],
    description: 'Our Version 1 Native App MVP service is designed for founders and product teams who need to move fast. We combine AI code generation with senior React Native engineers to ship a production-ready mobile app in just 15 days.\n\nYou get a fully functional, beautifully designed app with authentication, API integrations, and the core features your users need \u2014 all built on a scalable architecture ready for growth.',
    techStack: ['React Native', 'Node.js', 'PostgreSQL', 'AWS', 'Firebase'],
    process: [
      { step: 'Day 1-2', title: 'Scope & Architecture', desc: 'Requirements gathering, tech spec, and AI-assisted architecture design.' },
      { step: 'Day 3-4', title: 'UI/UX Design', desc: 'High-fidelity Figma designs with your approval before any code.' },
      { step: 'Day 5-12', title: 'AI-Accelerated Build', desc: 'AI generates boilerplate; senior devs write custom logic & integrations.' },
      { step: 'Day 13-14', title: 'QA & Testing', desc: 'Automated + manual testing across devices and edge cases.' },
      { step: 'Day 15', title: 'Deploy & Handoff', desc: 'App store submission, source code delivery, and documentation.' },
    ],
  },
  {
    id: 'enterprise-web-platform',
    title: 'Enterprise Web Platform',
    shortDesc: 'Production-grade web application delivered in 48 hours.',
    deliveryDays: 2,
    category: 'Web Platform',
    tiers: [
      {
        name: 'Basic MVP',
        pricePHP: 49975,  originalPricePHP: 99950,
        priceUSD: 999,    originalPriceUSD: 1999,
        delivery: '2 days',
        features: [
          'Up to 5 pages / views',
          'Responsive design',
          'CMS integration',
          'Contact form & analytics',
          '1 round of revisions',
          'Source code handoff',
        ],
      },
      {
        name: 'Pro Scaling',
        pricePHP: 124975, originalPricePHP: 249950,
        priceUSD: 2499,   originalPriceUSD: 4999,
        delivery: '5 days',
        features: [
          'Up to 15 pages / views',
          'Custom dashboard & admin panel',
          'Auth + role-based access',
          'API integrations (up to 3)',
          '3 rounds of revisions',
          'SEO optimization',
          '30-day support',
        ],
      },
      {
        name: 'Enterprise',
        pricePHP: 324975, originalPricePHP: 649950,
        priceUSD: 6499,   originalPriceUSD: 12999,
        delivery: '14 days',
        features: [
          'Unlimited pages & features',
          'Multi-tenant architecture',
          'SSO + advanced security',
          'Custom API development',
          'Unlimited revisions',
          'Performance optimization',
          '90-day support',
          'SLA guarantee',
        ],
      },
    ],
    description: 'Need a web platform yesterday? Our Enterprise Web Platform service delivers production-grade web applications in as little as 48 hours. Built with modern frameworks and AI-accelerated workflows, your platform ships fast without sacrificing quality.\n\nFrom marketing sites to complex dashboards, we handle the full stack \u2014 design, development, deployment \u2014 so you can focus on your business.',
    techStack: ['Next.js', 'React', 'Tailwind CSS', 'Node.js', 'MongoDB'],
    process: [
      { step: 'Hour 0-4', title: 'Scope & Design', desc: 'AI-assisted wireframes and design system setup.' },
      { step: 'Hour 4-24', title: 'Core Build', desc: 'AI scaffolding + senior dev custom implementation.' },
      { step: 'Hour 24-40', title: 'Integration & Polish', desc: 'API connections, animations, and responsive testing.' },
      { step: 'Hour 40-48', title: 'Deploy & Handoff', desc: 'Production deployment, DNS, SSL, and documentation.' },
    ],
  },
  {
    id: 'full-stack-application',
    title: 'Full Stack Application',
    shortDesc: 'End-to-end custom software built to your exact specifications.',
    deliveryDays: null,
    category: 'Custom Build',
    tiers: [
      {
        name: 'Basic MVP',
        pricePHP: 199975, originalPricePHP: 399950,
        priceUSD: 3999,   originalPriceUSD: 7999,
        delivery: '3 weeks',
        features: [
          'Custom architecture design',
          'Frontend + backend development',
          'Database design & setup',
          'Basic API integrations',
          '2 rounds of revisions',
          'Source code + docs',
        ],
      },
      {
        name: 'Pro Scaling',
        pricePHP: 374975, originalPricePHP: 749950,
        priceUSD: 7499,   originalPriceUSD: 14999,
        delivery: '6 weeks',
        features: [
          'Complex feature development',
          'Microservices or monolith',
          'Advanced integrations (5+)',
          'Real-time features (WebSockets)',
          '5 rounds of revisions',
          'CI/CD + monitoring',
          '60-day support',
        ],
      },
      {
        name: 'Enterprise',
        pricePHP: 749975, originalPricePHP: 1499950,
        priceUSD: 14999,  originalPriceUSD: 29999,
        delivery: '8-12 weeks',
        features: [
          'Enterprise-grade architecture',
          'Multi-service ecosystem',
          'ML/AI feature integration',
          'Advanced security & compliance',
          'Unlimited revisions',
          'Full DevOps setup',
          '6-month support',
          'Dedicated team',
        ],
      },
    ],
    description: "When off-the-shelf won't cut it, our Full Stack Application service delivers bespoke software built exactly to your specifications. Whether you need a SaaS platform, marketplace, internal tool, or something entirely new \u2014 we architect and build it from the ground up.\n\nOur senior engineers work alongside AI acceleration to deliver complex applications in a fraction of traditional timelines.",
    techStack: ['React', 'Node.js', 'Python', 'PostgreSQL', 'Redis', 'Docker', 'Kubernetes'],
    process: [
      { step: 'Week 1', title: 'Discovery & Architecture', desc: 'Deep-dive into requirements, system design, and tech decisions.' },
      { step: 'Week 2', title: 'Design & Prototype', desc: 'Interactive prototypes and design system creation.' },
      { step: 'Week 3-6', title: 'Iterative Build', desc: 'Sprint-based development with weekly demos and feedback.' },
      { step: 'Final', title: 'QA, Deploy & Support', desc: 'Comprehensive testing, production deployment, and ongoing support.' },
    ],
  },
  {
    id: 'managed-hosting',
    title: 'Managed Hosting & App Deployment',
    shortDesc: 'Enterprise-grade cloud hosting for your web platform, plus full iOS (App Store) and Android (Google Play) deployment management.',
    deliveryDays: null,
    category: 'Hosting',
    isRecurring: true,
    tiers: [
      {
        name: 'Starter',
        pricePHP: 24975,  originalPricePHP: null,
        priceUSD: 499,    originalPriceUSD: null,
        delivery: 'Monthly',
        recurring: true,
        features: [
          '99.9% Uptime Guarantee',
          'Automated Daily Backups',
          'App Store Submission & Approval',
          'SSL & Security Updates',
          'Basic CDN & Caching',
          'Email support',
        ],
      },
      {
        name: 'Growth',
        pricePHP: 49975,  originalPricePHP: null,
        priceUSD: 999,    originalPriceUSD: null,
        delivery: 'Monthly',
        recurring: true,
        features: [
          'Everything in Starter',
          'Auto-scaling infrastructure',
          'CI/CD pipeline management',
          'App Store updates & versioning',
          'Performance monitoring & alerts',
          'Priority Slack support',
          'Monthly health reports',
        ],
      },
      {
        name: 'Enterprise',
        pricePHP: 124975, originalPricePHP: null,
        priceUSD: 2499,   originalPriceUSD: null,
        delivery: 'Monthly',
        recurring: true,
        features: [
          'Everything in Growth',
          'Dedicated DevOps engineer',
          'Multi-region deployment',
          'Custom SLA (99.99%)',
          'Disaster recovery plan',
          'Compliance assistance (SOC2, HIPAA)',
          '24/7 on-call support',
          'Quarterly architecture reviews',
        ],
      },
    ],
    description: "Focus on your product while we handle the infrastructure. Our Managed Hosting & App Deployment service gives you enterprise-grade cloud hosting with automated backups, SSL management, and full app store deployment \u2014 from submission to approval.\n\nWhether you're scaling a web platform or managing iOS and Android releases, our DevOps team ensures your applications stay fast, secure, and always online.",
    techStack: ['AWS', 'Google Cloud', 'Docker', 'Kubernetes', 'Terraform', 'CloudFlare'],
    process: [
      { step: 'Day 1', title: 'Infrastructure Audit', desc: 'Review your current stack and design the optimal hosting architecture.' },
      { step: 'Day 2-3', title: 'Migration & Setup', desc: 'Set up cloud infrastructure, CI/CD, monitoring, and backups.' },
      { step: 'Day 4-5', title: 'App Store Deploy', desc: 'Submit apps to App Store / Google Play with optimized metadata.' },
      { step: 'Ongoing', title: 'Managed Operations', desc: 'Continuous monitoring, updates, scaling, and support.' },
    ],
  },
  {
    id: 'virtual-assistant',
    title: 'Virtual Assistant & Staff Augmentation',
    shortDesc: 'Dedicated remote professionals for development, design, QA, and project management — by the hour.',
    deliveryDays: null,
    category: 'VA / Staffing',
    isRecurring: true,
    isHourly: true,
    tiers: [
      {
        name: 'Part-Time',
        pricePHP: 24975,  originalPricePHP: null,
        priceUSD: 499,    originalPriceUSD: null,
        delivery: '20 hrs/week',
        recurring: true,
        features: [
          '20 hours per week',
          'Dedicated developer or designer',
          'Daily standups & Slack access',
          'Task tracking via Jira/Trello',
          'Weekly progress reports',
          'Cancel anytime',
        ],
      },
      {
        name: 'Full-Time',
        pricePHP: 44975,  originalPricePHP: null,
        priceUSD: 899,    originalPriceUSD: null,
        delivery: '40 hrs/week',
        recurring: true,
        features: [
          '40 hours per week (full-time)',
          'Dedicated senior engineer',
          'Direct Slack & video access',
          'Sprint planning & retrospectives',
          'Code reviews included',
          'Monthly performance reviews',
          'Cancel with 2-week notice',
        ],
      },
      {
        name: 'Team',
        pricePHP: 124975, originalPricePHP: null,
        priceUSD: 2499,   originalPriceUSD: null,
        delivery: 'Custom hours',
        recurring: true,
        features: [
          'Multi-person dedicated team',
          'PM + Dev + QA + Designer',
          'Custom hours & schedule',
          'Dedicated Slack channel',
          'Sprint-based delivery',
          'SLA guarantee',
          'Quarterly reviews',
          'Scale up/down flexibly',
        ],
      },
    ],
    description: "Need ongoing development support without the overhead of hiring? Our Virtual Assistant & Staff Augmentation service provides dedicated remote professionals \u2014 developers, designers, QA engineers, and project managers \u2014 working directly with your team.\n\nWhether you need 20 hours a week or a full dedicated team, we match you with senior talent who integrate seamlessly into your workflow.",
    techStack: ['React', 'Node.js', 'Python', 'Figma', 'Jira', 'Slack'],
    process: [
      { step: 'Day 1', title: 'Requirements & Matching', desc: 'We understand your needs and match you with the right professionals.' },
      { step: 'Day 2-3', title: 'Onboarding', desc: 'Your dedicated team member gets access to your tools, repos, and workflows.' },
      { step: 'Week 1+', title: 'Active Engagement', desc: 'Daily standups, task delivery, and continuous communication.' },
      { step: 'Ongoing', title: 'Performance & Scaling', desc: 'Monthly reviews, performance tracking, and team scaling as needed.' },
    ],
  },
  {
    id: 'ai-marketing-automation',
    title: 'AI Marketing & Automation',
    shortDesc: "Stop posting manually. We build automated AI content pipelines and email sequences to put your startup's growth on autopilot.",
    deliveryDays: null,
    category: 'Growth & Ops',
    isRecurring: false,
    tiers: [
      {
        name: 'Automated Setup',
        pricePHP: 49975,  originalPricePHP: null,
        priceUSD: 999,    originalPriceUSD: null,
        delivery: 'One-time',
        recurring: false,
        features: [
          'Welcome email sequence (5-part)',
          'Zapier / Make.com integrations',
          'CRM wiring & pipeline setup',
          'Lead capture form automation',
          'Analytics dashboard setup',
          'Source documentation',
        ],
      },
      {
        name: 'Content Autopilot',
        pricePHP: 44975,  originalPricePHP: null,
        priceUSD: 899,    originalPriceUSD: null,
        delivery: 'Monthly',
        recurring: true,
        features: [
          'AI-generated social media calendar',
          'Automated posting (IG, X, LinkedIn, FB)',
          'Newsletter management & A/B testing',
          'Content repurposing pipeline',
          'Monthly performance reports',
          'Dedicated content manager',
          'Cancel anytime',
        ],
      },
      {
        name: 'Full Growth Engine',
        pricePHP: 74975,  originalPricePHP: null,
        priceUSD: 1499,   originalPriceUSD: null,
        delivery: 'Monthly',
        recurring: true,
        features: [
          'Everything in Content Autopilot',
          'Advanced cold outreach sequences',
          'AI-powered lead scoring',
          'Dedicated VA for lead management',
          'Custom CRM automations',
          'Conversion funnel optimization',
          'Weekly strategy calls',
          'Scale up/down flexibly',
        ],
      },
    ],
    description: "Your product is live but growth is stalling. Sound familiar? Most founders spend hours each week on manual marketing \u2014 posting to social media, sending emails, chasing leads. It's exhausting and it doesn't scale.\n\nOur AI Marketing & Automation service puts your growth on autopilot. We wire up AI-powered content pipelines, email sequences, and lead management systems that run 24/7 \u2014 so you can focus on building your product while your marketing machine runs itself.",
    techStack: ['Zapier', 'Make.com', 'HubSpot', 'Mailchimp', 'OpenAI', 'Meta Ads'],
    process: [
      { step: 'Week 1', title: 'Growth Audit', desc: 'We analyze your current channels, audience, and conversion funnels.' },
      { step: 'Week 2', title: 'Pipeline Build', desc: 'Set up automated email sequences, social posting, and CRM integrations.' },
      { step: 'Week 3', title: 'Content Engine', desc: 'AI generates your content calendar; we refine and schedule everything.' },
      { step: 'Ongoing', title: 'Optimize & Scale', desc: 'Monthly reporting, A/B testing, and continuous optimization.' },
    ],
  },
];

/* ── Currency-aware price helpers ── */
export function getTierPrice(tier, currency) {
  return currency === 'USD' ? tier.priceUSD : tier.pricePHP;
}
export function getTierOriginalPrice(tier, currency) {
  return currency === 'USD' ? tier.originalPriceUSD : tier.originalPricePHP;
}
export function getCurrencySymbol(currency) {
  return PRICING[currency]?.symbol || '₱';
}
export function formatPrice(amount, currency) {
  const sym = getCurrencySymbol(currency);
  return `${sym}${amount.toLocaleString()}`;
}

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);
  const [currency, setCurrency] = useState('USD');

  // DaaS plan configuration — fetched once from the authoritative backend config.
  const [plansConfig, setPlansConfig] = useState(DAAS_PLANS_FALLBACK);
  useEffect(() => {
    let active = true;
    axios.get(PLANS_API)
      .then((res) => { if (active && res.data?.plans) setPlansConfig(res.data); })
      .catch(() => { /* keep fallback */ });
    return () => { active = false; };
  }, []);

  const simulateCheckout = useCallback((gig, tierIndex) => {
    const tier = gig.tiers[tierIndex];
    const price = getTierPrice(tier, 'PHP');
    const order = {
      id: 'ORD-' + Date.now().toString(36).toUpperCase(),
      gig, tier, tierIndex,
      total: price,
      deposit: Math.round(price * 0.5),
      status: 'IN_DEVELOPMENT',
      phases: [
        { id: 'deposit', label: 'Deposit Paid', done: true },
        { id: 'architecture', label: 'AI Architecture & Scoping', done: false },
        { id: 'design', label: 'Vibe & Design Approval', done: false },
        { id: 'development', label: 'Development Phase', done: false },
        { id: 'review', label: 'Final Review', done: false },
      ],
      deliverables: [
        { id: 'd1', title: 'Information Architecture & Wireframes', status: 'pending', type: 'document' },
        { id: 'd2', title: 'High-Fidelity UI Mockups', status: 'pending', type: 'design' },
        { id: 'd3', title: 'API Architecture Document', status: 'pending', type: 'document' },
      ],
      createdAt: new Date().toISOString(),
    };
    setUser({ name: 'Demo Client', email: 'client@demo.com' });
    setActiveOrder(order);
    return order;
    // setUser, setActiveOrder are stable React state setters; getTierPrice is module-level
  }, [setUser, setActiveOrder]);

  const updateDeliverableStatus = useCallback((delivId, newStatus, feedback) => {
    setActiveOrder((prev) => {
      if (!prev) return prev;
      return { ...prev, deliverables: prev.deliverables.map((d) => d.id === delivId ? { ...d, status: newStatus, feedback: feedback || d.feedback } : d) };
    });
  }, [setActiveOrder]);

  const advancePhase = useCallback(() => {
    setActiveOrder((prev) => {
      if (!prev) return prev;
      const phases = prev.phases.map((p) => ({ ...p }));
      const nextIdx = phases.findIndex((p) => !p.done);
      if (nextIdx >= 0) phases[nextIdx].done = true;
      return { ...prev, phases };
    });
  }, [setActiveOrder]);

  return (
    <AppContext.Provider
      value={{ user, setUser, activeOrder, setActiveOrder, simulateCheckout, updateDeliverableStatus, advancePhase, currency, setCurrency, plansConfig, plans: plansConfig.plans }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be inside AppProvider');
  return ctx;
}
